import { CaretUpDownIcon, CheckIcon } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { PopoverSearchHeader } from "@/components/ui/popover-search-header";

interface SearchableSelectProps<T extends { id: number }> {
	items: T[];
	selected: T | null;
	onSelect: (item: T) => void;
	disabled?: boolean;
	placeholder: string;
	searchPlaceholder: string;
	emptyMessage: string;
	getLabel: (item: T) => string;
}

export function SearchableSelect<T extends { id: number }>({
	items,
	selected,
	onSelect,
	disabled,
	placeholder,
	searchPlaceholder,
	emptyMessage,
	getLabel,
}: SearchableSelectProps<T>) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const filtered = items.filter((item) =>
		getLabel(item).toLowerCase().includes(search.toLowerCase()),
	);

	function select(item: T) {
		onSelect(item);
		setSearch("");
		setOpen(false);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="w-full justify-between font-normal"
					disabled={disabled}
				>
					<span className={selected ? "" : "text-muted-foreground"}>
						{selected ? getLabel(selected) : placeholder}
					</span>
					<CaretUpDownIcon className="ml-2 size-4 shrink-0 text-muted-foreground" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-(--radix-popover-trigger-width) p-0"
				align="start"
				onOpenAutoFocus={(e) => {
					e.preventDefault();
					inputRef.current?.focus();
				}}
			>
				<PopoverSearchHeader
					ref={inputRef}
					value={search}
					onValueChange={setSearch}
					placeholder={searchPlaceholder}
				/>
				<div
					className="max-h-64 overflow-y-auto overscroll-contain p-1"
					onWheel={(e) => e.stopPropagation()}
					onTouchMove={(e) => e.stopPropagation()}
				>
					{filtered.map((item) => (
						<button
							type="button"
							key={item.id}
							className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
							onClick={() => select(item)}
						>
							<CheckIcon
								className={`size-4 shrink-0 ${selected?.id === item.id ? "opacity-100" : "opacity-0"}`}
							/>
							<span className="truncate">{getLabel(item)}</span>
						</button>
					))}
					{filtered.length === 0 && (
						<p className="py-4 text-center text-sm text-muted-foreground">
							{emptyMessage}
						</p>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
